import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Layout, 
  Card, 
  Avatar, 
  Typography, 
  Table, 
  Space, 
  Button, 
  Tag, 
  Row, 
  Col,
  Input,
  Statistic,
  Badge,
  Segmented,
  Modal
} from 'antd';
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  HomeOutlined,
  CalendarOutlined,
  BookOutlined,
  FileTextOutlined, 
  TrophyOutlined, 
  DeleteOutlined,
  EnvironmentOutlined,
  QuestionCircleOutlined,
  VideoCameraOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  ArrowRightOutlined,
  HistoryOutlined,
  StarOutlined
} from '@ant-design/icons';
import withRoleAccess from '../../hoc/withRoleAccess';
import Sidebar from '../../components/Sidebar/Sidebar';
import Header from '../../components/Header/Header';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import moment from 'moment';
import thLocale from '@fullcalendar/core/locales/th';
import { useTranslation } from 'react-i18next';
import './Student.css';

const { Content } = Layout;
const { Title, Text } = Typography;
const { TextArea } = Input;
const { confirm } = Modal;

function StudentDashboardAdmin() {
  // Existing state declarations remain the same
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [student, setStudent] = useState(null);
  const [error, setError] = useState(null);
  const [attendanceLogs, setAttendanceLogs] = useState([]);
  const [billingHistory, setBillingHistory] = useState([]);
  const [testScores, setTestScores] = useState([]);
  const [studentInfo, setStudentInfo] = useState([]);
  const [newStudentInfo, setNewStudentInfo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [classChangeLogs, setClassChangeLogs] = useState([]);
  const [scheduleView, setScheduleView] = useState('upcoming'); // 'upcoming' or 'past'
  const itemsPerPage = 10; // Reduced for better mobile experience

  const API_URL = process.env.REACT_APP_API_URL;
  
  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':');
    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
  };
  
  const platformMap = {
    athome: {
      label: t('athome'),
      icon: <HomeOutlined />,
      color: 'orange',
    },
    online: {
      label: t('online'),
      icon: <VideoCameraOutlined />,
      color: 'green',
    },
    onsite: {
      label: t('onsite'),
      icon: <EnvironmentOutlined />,
      color: 'blue',
    },
  };

  // All existing useEffect hooks remain the same
  useEffect(() => {
    const fetchStudentData = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/students/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const studentData = response.data; 

        let photoUrl = 'https://via.placeholder.com/150';

        if (studentData.profile_photo) {
          const baseUrl = API_URL.replace(/\/api.*$/, '').replace(/\/$/, '');
          if (studentData.profile_photo.startsWith('/')) {
            photoUrl = `${baseUrl}${studentData.profile_photo}`;
          } else if (studentData.profile_photo.startsWith('http')) {
            photoUrl = studentData.profile_photo;
          }
        }
        
        setStudent({
          ...studentData,
          photoUrl: photoUrl,
        });
      } catch (error) {
        console.error('Error fetching student data:', error);
        setError('Failed to fetch student data.');
      }
    };

    const fetchTestScores = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/test-scores/student/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTestScores(response.data);
      } catch (error) {
        console.error('Error fetching test scores:', error);
      }
    };

    const fetchStudentInfo = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/student-info/student/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStudentInfo(response.data);
      } catch (error) {
        console.error('Error fetching student info:', error);
      }
    };

    const fetchAttendanceLogs = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/attendance/student/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAttendanceLogs(response.data);
      } catch (error) {
        console.error('Error fetching attendance logs:', error);
      }
    };

    const fetchBillingHistory = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/registrations/student/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const { registrations } = response.data;

        const sortedRegistrations = registrations.map(reg => {
          const sortedSchedule = reg.schedule.sort((a, b) => {
            const dateTimeA = moment(`${a.date} ${a.time}`, 'YYYY-MM-DD HH:mm:ss');
            const dateTimeB = moment(`${b.date} ${b.time}`, 'YYYY-MM-DD HH:mm:ss');
            return dateTimeA - dateTimeB;
          });
          return { ...reg, schedule: sortedSchedule };
        });

        sortedRegistrations.sort((a, b) => {
          const earliestA = a.schedule[0] ? moment(`${a.schedule[0].date} ${a.schedule[0].time}`, 'YYYY-MM-DD HH:mm:ss') : moment(0);
          const earliestB = b.schedule[0] ? moment(`${b.schedule[0].date} ${b.schedule[0].time}`, 'YYYY-MM-DD HH:mm:ss') : moment(0);
          return earliestA - earliestB;
        });

        setBillingHistory(sortedRegistrations);
      } catch (error) {
        console.error('Error fetching billing history:', error);
      }
    };

    fetchStudentData();
    fetchTestScores();
    fetchStudentInfo();
    fetchAttendanceLogs();
    fetchBillingHistory();
  }, [id, API_URL]);

  useEffect(() => {
    const fetchClassChangeLogs = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/class-change/class-change-logs`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setClassChangeLogs(response.data);
      } catch (error) {
        console.error('Error fetching class change logs:', error);
      }
    };
    fetchClassChangeLogs();
  }, [API_URL]);

  // All existing handler functions remain the same
  const handleAddStudentInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/student-info`, {
        student_id: id,
        info_text: newStudentInfo
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStudentInfo([response.data, ...studentInfo]);
      setNewStudentInfo('');
    } catch (error) {
      console.error('Error adding student info:', error);
    }
  };

const handleDeleteStudentInfo = async (id) => {
  confirm({
    title: 'Are you sure you want to delete this student information?',
    content: 'This action cannot be undone.',
    okText: 'Yes, delete it',
    okType: 'danger',
    cancelText: 'Cancel',
    onOk: async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.delete(`${API_URL}/student-info/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.status === 200) {
          setStudentInfo(studentInfo.filter(info => info.id !== id));
        }
      } catch (error) {
        console.error('Error deleting student info:', error);
      }
    }
  });
};


  const calculateEndTime = (startTime, duration) => {
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const [durationHours, durationMinutes] = duration.split(':').map(Number);
    let endHours = startHours + durationHours;
    let endMinutes = startMinutes + durationMinutes;
    if (endMinutes >= 60) {
      endHours += Math.floor(endMinutes / 60);
      endMinutes %= 60;
    }
    return `${endHours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
  };

  const formatDate = useCallback((dateString) => {
    const date = new Date(dateString);
    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    return new Intl.DateTimeFormat(i18n.language, options).format(date).toUpperCase();
  }, [i18n.language]);

  // Calculate statistics
  const stats = useMemo(() => {
    const allClasses = billingHistory.flatMap(enrollment => enrollment.schedule);
    const now = moment();
    const upcomingClasses = allClasses.filter(cls => moment(`${cls.date} ${cls.time}`, 'YYYY-MM-DD HH:mm:ss').isAfter(now));
    const pastClasses = allClasses.filter(cls => moment(`${cls.date} ${cls.time}`, 'YYYY-MM-DD HH:mm:ss').isBefore(now));
    
    const totalClasses = allClasses.length;
    const attendedClasses = attendanceLogs.filter(log => log.status === 'Present').length;
    const totalHours = billingHistory.reduce((sum, enrollment) => sum + parseFloat(enrollment.total_hours || 0), 0);
    const averageScore = testScores.length > 0 ? 
      (testScores.reduce((sum, score) => sum + parseFloat(score.score || 0), 0) / testScores.length).toFixed(1) : 0;

    return {
      totalClasses,
      upcomingClasses: upcomingClasses.length,
      pastClasses: pastClasses.length,
      attendedClasses,
      totalHours,
      averageScore,
      attendanceRate: totalClasses > 0 ? ((attendedClasses / totalClasses) * 100).toFixed(1) : 0
    };
  }, [billingHistory, attendanceLogs, testScores]);

  // Separate upcoming and past classes
  const { upcomingSchedule, pastSchedule } = useMemo(() => {
    const allSchedule = billingHistory
      .flatMap(enrollment =>
        enrollment.schedule.map(schedule => ({
          ...schedule,
          class_code: enrollment.class_code,
          platform: schedule.platform 
        }))
      );
    
    const now = moment();
    const upcoming = allSchedule
      .filter(cls => moment(`${cls.date} ${cls.time}`, 'YYYY-MM-DD HH:mm:ss').isAfter(now))
      .sort((a, b) => moment(`${a.date} ${a.time}`, 'YYYY-MM-DD HH:mm:ss') - moment(`${b.date} ${b.time}`, 'YYYY-MM-DD HH:mm:ss'));
    
    const past = allSchedule
      .filter(cls => moment(`${cls.date} ${cls.time}`, 'YYYY-MM-DD HH:mm:ss').isBefore(now))
      .sort((a, b) => moment(`${b.date} ${b.time}`, 'YYYY-MM-DD HH:mm:ss') - moment(`${a.date} ${a.time}`, 'YYYY-MM-DD HH:mm:ss'));

    return { upcomingSchedule: upcoming, pastSchedule: past };
  }, [billingHistory]);

  const currentScheduleData = scheduleView === 'upcoming' ? upcomingSchedule : pastSchedule;
  const paginatedSchedule = useMemo(() => {
    return currentScheduleData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  }, [currentScheduleData, currentPage, itemsPerPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleScheduleViewChange = (view) => {
    setScheduleView(view);
    setCurrentPage(1); // Reset to first page when switching views
  };

 const eventContent = (eventInfo) => {
  const startStr = eventInfo.event.startStr || '';
  const endStr = eventInfo.event.endStr || '';

  const startTime = startStr.includes('T') 
    ? startStr.split('T')[1].slice(0, 5) 
    : '';
  const endTime = endStr.includes('T') 
    ? endStr.split('T')[1].slice(0, 5) 
    : '';

  const { subject, teacher, classroom } = eventInfo.event.extendedProps || {};

  return (
    <div style={{ padding: '4px', fontSize: '0.75em', lineHeight: '1.2' }}>
      {startTime && endTime && (
        <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>
          {`${startTime} - ${endTime}`}
        </div>
      )}
      <div style={{ marginBottom: '1px' }}>{eventInfo.event.title}</div>
      {subject && <div style={{ marginBottom: '1px' }}>{subject}</div>}
      {teacher && <div style={{ marginBottom: '1px' }}>{teacher}</div>}
      {classroom && <div>{`Room: ${classroom}`}</div>}
    </div>
  );
};


const handlePrintPDF = async () => {
  const calendarElement = document.getElementById('calendar-container');
  if (!calendarElement || !student) return;

  // Disable ALL transitions and animations temporarily
  const style = document.createElement('style');
  style.id = 'pdf-export-disable-animations';
  style.textContent = `
    *, *::before, *::after {
      transition: none !important;
      animation: none !important;
      opacity: 1 !important;
    }
  `;
  document.head.appendChild(style);

  // Force a reflow to apply the styles
  void calendarElement.offsetHeight;

  // Wait for any ongoing animations to settle
  await new Promise(resolve => setTimeout(resolve, 100));

  try {
    const canvas = await html2canvas(calendarElement, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');

    const pdf = new jsPDF('landscape', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;
      let yOffset = 12;
   try {
  // Load the school logo
  const logoImg = new Image();
  logoImg.crossOrigin = 'anonymous';
  
  await new Promise((resolve, reject) => {
    logoImg.onload = resolve;
    logoImg.onerror = reject;
    logoImg.src = '/images/keaes.png';
  });

  // Convert logo to base64
  const logoCanvas = document.createElement('canvas');
  const logoCtx = logoCanvas.getContext('2d');
  logoCanvas.width = logoImg.width;
  logoCanvas.height = logoImg.height;
  logoCtx.drawImage(logoImg, 0, 0);
  const logoDataUrl = logoCanvas.toDataURL('image/png');

    // Header background
    pdf.setFillColor(248, 249, 250);
    pdf.rect(0, 0, pageWidth, 35, 'F');

 
  // Add logo in the center
  const logoMaxWidth = 25;
  const logoMaxHeight = 20;
  
  // Calculate proportional size
  const logoAspectRatio = logoImg.width / logoImg.height;
  let logoWidth, logoHeight;
  
  if (logoAspectRatio > logoMaxWidth / logoMaxHeight) {
    logoWidth = logoMaxWidth;
    logoHeight = logoMaxWidth / logoAspectRatio;
  } else {
    logoHeight = logoMaxHeight;
    logoWidth = logoMaxHeight * logoAspectRatio;
  }
  
  // Center the logo
  const logoX = (pageWidth - logoWidth) / 2;
  const logoY = 7;
  pdf.addImage(logoDataUrl, 'PNG', logoX, logoY, logoWidth, logoHeight);

  // Student name (left side)
  let yOffset = 12;
  pdf.setFont('Helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.setTextColor(33, 37, 41);
  pdf.text(`${student.firstname} ${student.lastname}`, margin, yOffset);


  // Add current date to the right
  pdf.setFont('Helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(108, 117, 125);
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  const dateText = `Generated: ${currentDate}`;
  const dateWidth = pdf.getTextWidth(dateText);
  pdf.text(dateText, pageWidth - margin - dateWidth, 12);

  // Add current time under date (right side)
  const currentTime = new Date().toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit'
  });
  const timeText = `Time: ${currentTime}`;
  const timeWidth = pdf.getTextWidth(timeText);
  pdf.text(timeText, pageWidth - margin - timeWidth, 20);

} catch (logoError) {
  console.warn('Could not load logo, proceeding without it:', logoError);
  
  // Fallback header without logo
  pdf.setFillColor(248, 249, 250);
  pdf.rect(0, 0, pageWidth, 35, 'F');

  // Student name (left side)
    let yOffset = 12;
    pdf.setFont('Helvetica', 'bold');
    pdf.setFontSize(20);
    pdf.setTextColor(33, 37, 41);
    pdf.text(`${student.firstname} ${student.lastname}`, margin, yOffset);

    // Add current date to the right
    pdf.setFont('Helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(108, 117, 125);
    const currentDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    const dateText = `Generated: ${currentDate}`;
    const dateWidth = pdf.getTextWidth(dateText);
    pdf.text(dateText, pageWidth - margin - dateWidth, yOffset);
    }
  
    // Student info section
    yOffset += 8;
    pdf.setFont('Helvetica', 'normal');
    pdf.setFontSize(11);
  
    // Create info badges/tags with better styling
    const drawInfoBadge = (text, x, y, color, bgColor = null) => {
      pdf.setTextColor(...color);
      if (bgColor) {
        const textWidth = pdf.getTextWidth(text);
        pdf.setFillColor(...bgColor);
        pdf.roundedRect(x - 2, y - 3, textWidth + 4, 5, 1, 1, 'F');
      }
      pdf.text(text, x, y);
    };

    // Contact information section
    yOffset += 9;
    pdf.setTextColor(73, 80, 87);
    pdf.setFont('Helvetica', 'normal');
    pdf.setFontSize(10);

    const contactInfo = [];
    if (student.email) contactInfo.push(`${student.email}`);
    if (student.phone) contactInfo.push(`${student.phone}`);
    
    if (contactInfo.length > 0) {
      pdf.text(contactInfo.join('  •  '), margin, yOffset);
    }

    // Separator line
    yOffset += 6;
    pdf.setDrawColor(222, 226, 230);
    pdf.setLineWidth(0.5);
    pdf.line(margin, yOffset, pageWidth - margin, yOffset);

    // Calendar title
    yOffset += 8;
    pdf.setFont('Helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.setTextColor(33, 37, 41);
    pdf.text('Academic Calendar', margin, yOffset);

    // Calendar image
    yOffset += 8;

    const availableWidth = pageWidth - margin * 2;
    const availableHeight = pageHeight - yOffset - margin;

    const imgProps = pdf.getImageProperties(imgData);
    const ratio = Math.min(availableWidth / imgProps.width, availableHeight / imgProps.height);
    const imgWidth = imgProps.width * ratio;
    const imgHeight = imgProps.height * ratio;

    const x = (pageWidth - imgWidth) / 2;
    const y = yOffset;

    // Border around calendar
    pdf.setDrawColor(222, 226, 230);
    pdf.setLineWidth(0.3);
    pdf.rect(x - 1, y - 1, imgWidth + 2, imgHeight + 2);

    pdf.addImage(imgData, 'PNG', x, y, imgWidth, imgHeight);

    // Footer
    pdf.setFont('Helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(108, 117, 125);
    const footerText = 'This calendar is generated automatically and subject to updates. © KEAES ACADEMICS';
    const footerWidth = pdf.getTextWidth(footerText);
    pdf.text(footerText, (pageWidth - footerWidth) / 2, pageHeight - 5);

    pdf.save(`${student.firstname || 'student'}-calendar-${new Date().toISOString().split('T')[0]}.pdf`);

  } finally {
    // Remove the temporary style - ALWAYS clean up
    const tempStyle = document.getElementById('pdf-export-disable-animations');
    if (tempStyle) {
      tempStyle.remove();
    }
  }
};
  

const getChangeReasonDisplay = (reason) => {
  switch (reason) {
    case "admin_adjust":
      return { text: "Admin", color: "#678d5b" };
    case "cancelled_by_student":
      return { text: "CL by S.", color: "red" };
    case "cancelled_by_teacher":
      return { text: "CL by T.", color: "orange" };
    case "class_on_hold_by_student":
      return { text: "On hold by S.", color: "blue" };
    case "class_on_hold_by_school":
      return { text: "On hold by P.", color: "green" };
    case "cancelled_same_day_teacher":
      return { text: "CSD (Teacher)", color: "#FF4500" };
    case "cancelled_same_day_student":
      return { text: "CSD (Student)", color: "#FF4500" };
    case "teacher_late":
      return { text: "Teacher Late", color: "gold" };
    case "kease_cut":
      return { text: "KEAES Cut", color: "magenta" };
    case "make_up":
      return { text: "Make Up", color: "#8A2BE2" };
    case "others":
      return { text: "Others", color: "darkgrey" };
    default:
      return { text: reason || "", color: "black" };
  }
};

                // Mobile-friendly table columns
                const scheduleColumns = [
                  {
                    title: t('date'),
                    dataIndex: 'date',
                    key: 'date',
                    render: (date) => (
                      <div>
                        <div style={{ fontWeight: 'bold' }}>{formatDate(date)}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {moment(date).format('dddd')}
                        </div>
                      </div>
                    ),
                    width: 120,
                  },
                  { title: t('classId'), dataIndex: 'classid' },
                  {
                    title: t('class'),
                    key: 'class_info',
                    render: (_, record) => (
                      <div>
                        <div style={{ fontSize: '11px', marginBottom: '4px' }}>{record.class_code}</div>
                        <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{record.subject}</div>
                      </div>
                    ),
                    width: 250,
                    
                  },
                        {
                    title: t('teacher'),
                    dataIndex: 'teacher',
                    key: 'teacher'
                  },
                  {
                    title: t('time'),
                    key: 'time_info',
                    render: (_, record) => (
                      <div>
                        <div style={{ fontWeight: 'bold' }}>
                          {`${formatTime(record.time)} - ${calculateEndTime(record.time, record.hours)}`}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>{record.hours} {t('hr')}</div>
                      </div>
                    ),
                    width: 120,
                  },
                {
                  title: t('platform'),
                  key: 'platform',
                  render: (_, record) => {
                    const config = platformMap[record.platform];
                    if (!config) {
                      return (
                        <Tag color="default" icon={<QuestionCircleOutlined />}>
                          {record.platform || '-'}
                        </Tag>
                      );
                    }

                    return (
                      <Tag color={config.color} icon={config.icon}>
                        {config.label}
                      </Tag>
                    );
                  }
                },

                  {
                    title: t('attendance'),
                    key: 'attendance',
                    render: (text, record) => {
                      const attendanceLog = attendanceLogs.find(log => log.classid === record.classid);
                      const status = attendanceLog?.status || 'Not Available';
                      
                      const statusStyles = {
                        Present: {
                          color: '#389e0d',
                          backgroundColor: '#f6ffed',
                          border: '1px solid #b7eb8f'
                        },
                        Absent: {
                          color: '#cf1322',
                          backgroundColor: '#fff1f0',
                          border: '1px solid #ffa39e'
                        },
                        Late: {
                          color: '#d46b08',
                          backgroundColor: '#fff7e6',
                          border: '1px solid #ffd591'
                        },
                        'Not Available': {
                          color: '#8c8c8c',
                          backgroundColor: '#f5f5f5',
                          border: '1px solid #d9d9d9'
                        }
                      };
                  
                      return (
                        <Tag
                          style={{
                            ...statusStyles[status],
                            borderRadius: '12px',
                            padding: '2px 12px',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                        >
                          {status}
                        </Tag>
                      );
                    }
                  },
                  {
                    title: t('homework'),
                    key: 'homework',
                    render: (text, record) => {
                      const attendanceLog = attendanceLogs.find(log => log.classid === record.classid);
                      const homework = attendanceLog?.homework || 'N/A';
                      
                      const homeworkStyles = {
                        YES: {
                          color: '#389e0d',
                          backgroundColor: '#f6ffed',
                          border: '1px solid #b7eb8f'
                        },
                        NO: {
                          color: '#cf1322',
                          backgroundColor: '#fff1f0',
                          border: '1px solid #ffa39e'
                        },
                        'N/A': {
                          color: '#8c8c8c',
                          backgroundColor: '#f5f5f5',
                          border: '1px solid #d9d9d9'
                        }
                      };
                  
                      return (
                        <Tag
                          style={{
                            ...homeworkStyles[homework],
                            borderRadius: '12px',
                            padding: '2px 12px',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                        >
                          {homework}
                        </Tag>
                      );
                    }
                  },
                  {
                    title: t('comments'),
                    key: 'comments',
                    width: 250,
                    render: (text, record) => {
                      const attendanceLog = attendanceLogs.find(log => log.classid === record.classid);
                      return (
                        <div 
                          style={{ 
                            whiteSpace: 'pre-wrap',
                            wordWrap: 'break-word',
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            minWidth: '150px',
                            maxWidth: '250px',
                            lineHeight: '1.5'
                          }}
                        >
                          {attendanceLog?.comment || '-'}
                        </div>
                      );
                    }
                  },
                  {
                                title: t('classchange'),
                                key: 'changereason',
                                render: (text, record) => {
                                  const log = classChangeLogs.find(log => log.classid === record.classid);
                                  const { text: changeReasonText, color } = getChangeReasonDisplay(log?.changereason);
                                  return <Tag color={color}>{changeReasonText}</Tag>;
                                }
                              },
  ];

  if (error) return <p>{error}</p>;
  if (!student) return <p>Loading...</p>;

  return (
    <Layout className="student-dashboard-layout">
      <Sidebar />
      <Layout>
        <Header />
        <Content style={{ padding: '12px 16px', background: '#f5f5f5' }}>
          {/* Student Header - Compact & Responsive */}
          <Card 
            style={{ 
              marginBottom: '16px',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
            }}
          >
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} sm={8} md={6}>
                <div style={{ textAlign: 'center' }}>
                  <Avatar 
                    size={80} 
                    src={student.photoUrl} 
                    icon={<UserOutlined />}
                    style={{
                      border: '3px solid #fff',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      marginBottom: '8px'
                    }}
                  />
                </div>
              </Col>
              
              <Col xs={24} sm={16} md={12}>
                <div>
                  <Title level={3} style={{ margin: '0 0 8px 0', fontSize: '1.5rem' }}>
                    {student?.firstname} {student?.lastname}
                  </Title>
                  <Space wrap size="small">
                    <Tag color="blue">{student?.schoolid}</Tag>
                    <Tag color="green">{student?.currenteducationlevel}</Tag>
                  </Space>
                  <div style={{ marginTop: '8px' }}>
                    <Space wrap size="small">
                      <Space size="small">
                        <MailOutlined style={{ color: '#1890ff' }} />
                        <Text type="secondary" style={{ fontSize: '12px' }}>{student?.email}</Text>
                      </Space>
                      <Space size="small">
                        <PhoneOutlined style={{ color: '#52c41a' }} />
                        <Text type="secondary" style={{ fontSize: '12px' }}>{student?.phone}</Text>
                      </Space>
                    </Space>
                  </div>
                </div>
              </Col>
              
              <Col xs={24} sm={24} md={6}>
                <Row gutter={[8, 8]}>
                  <Col xs={12} sm={6} md={12}>
                    <Statistic
                      title={t('Total Classes')}
                      value={stats.totalClasses}
                      prefix={<CalendarOutlined />}
                      valueStyle={{ fontSize: '16px', color: '#1890ff' }}
                    />
                  </Col>
                  <Col xs={12} sm={6} md={12}>
                    <Statistic
                      title={t('Attendance')}
                      value={`${stats.attendanceRate}%`}
                      prefix={<ClockCircleOutlined />}
                      valueStyle={{ 
                        fontSize: '16px', 
                        color: stats.attendanceRate >= 80 ? '#52c41a' : stats.attendanceRate >= 60 ? '#faad14' : '#ff4d4f' 
                      }}
                    />
                  </Col>
                  <Col xs={12} sm={6} md={12}>
                    <Statistic
                      title={t('Upcoming')}
                      value={stats.upcomingClasses}
                      prefix={<ArrowRightOutlined />}
                      valueStyle={{ fontSize: '16px', color: '#722ed1' }}
                    />
                  </Col>
                  <Col xs={12} sm={6} md={12}>
                    <Statistic
                      title={t('Avg Score')}
                      value={stats.averageScore}
                      prefix={<TrophyOutlined />}
                      valueStyle={{ fontSize: '16px', color: '#faad14' }}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </Card>

          {/* Calendar Section */}
           <Card
              title={
                <Space>
                  <CalendarOutlined />
                  {t('calendar')}
                  <Badge count={stats.totalClasses} showZero color="#1890ff" />
                </Space>
              }
              extra={
                <Button onClick={handlePrintPDF} type="primary">
                  Download Calendar PDF
                </Button>
              }
              style={{ marginBottom: '16px', borderRadius: '12px' }}
            >
            <div id="calendar-container" style={{ background: '#fff', borderRadius: '8px' }}>
              <FullCalendar
                plugins={[dayGridPlugin, timeGridPlugin]}
                initialView="dayGridMonth"
                locale={i18n.language === 'th' ? thLocale : 'en'}
                events={billingHistory.flatMap(enrollment =>
                  enrollment.schedule.map(cls => ({
                    title: `${enrollment.class_code}`,
                    start: `${cls.date}T${cls.time}`,
                    end: `${cls.date}T${calculateEndTime(cls.time, cls.hours)}`,
                    backgroundColor: moment(`${cls.date} ${cls.time}`, 'YYYY-MM-DD HH:mm:ss').isAfter(moment()) ? '#678d5b' : '#8c8c8c',
                    borderColor: moment(`${cls.date} ${cls.time}`, 'YYYY-MM-DD HH:mm:ss').isAfter(moment()) ? '#678d5b' : '#8c8c8c',
                    textColor: '#ffffff',
                    display: 'block',
                    extendedProps: {
                      subject: cls.subject,
                      teacher: cls.teacher,
                      classroom: cls.classroom,
                    },
                  }))
                )}
                eventContent={eventContent}
                headerToolbar={{
                  left: 'prev,next today',
                  center: 'title',
                  right: 'dayGridMonth,timeGridWeek',
                }}
                slotLabelFormat={{
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false, 
                }}
                height="auto"
                eventDisplay="block"
                moreLinkClick="popover"
              />
            </div>
          </Card>

          {/* Class Schedule Section */}
          <Card 
            title={
              <Space>
                <BookOutlined />
                {t('classSchedule')}
              </Space>
            }
            extra={
              <Segmented
                value={scheduleView}
                onChange={handleScheduleViewChange}
                options={[
                  { 
                    label: (
                      <Space>
                        <ArrowRightOutlined />
                        {t('Upcoming')}
                        <Badge count={stats.upcomingClasses} color="#722ed1" />
                      </Space>
                    ), 
                    value: 'upcoming' 
                  },
                  { 
                    label: (
                      <Space>
                        <HistoryOutlined />
                        {t('Past')}
                        <Badge count={stats.pastClasses} color="#8c8c8c" />
                      </Space>
                    ), 
                    value: 'past' 
                  }
                ]}
              />
            }
            style={{ marginBottom: '16px', borderRadius: '12px' }}
          >
            <Table
              columns={scheduleColumns}
              dataSource={paginatedSchedule}
              pagination={{
                current: currentPage,
                total: currentScheduleData.length,
                pageSize: itemsPerPage,
                onChange: handlePageChange,
                showSizeChanger: false,
                showQuickJumper: false,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} classes`,
                size: 'small'
              }}
              scroll={{ x: 'max-content' }}
              style={{ overflowX: 'auto', marginTop: '20px' }}
              size="small"
              rowKey="classid"
            />
          </Card>
<Row gutter={[16, 16]}>
  {/* Current Enrollments - Full Width */}
  <Col xs={24}>
    <Card 
      title={
        <Space>
          <TeamOutlined />
          {t('currentEnrollments')}
          <Badge count={billingHistory.length} color="#722ed1" />
        </Space>
      }
      style={{ borderRadius: '12px' }}
      bodyStyle={{ maxHeight: '400px', overflowY: 'auto' }}
    >
      <Row gutter={[12, 12]}>
        {billingHistory.map((enrollment, index) => (
          <Col xs={24} sm={12} md={8} lg={6} xl={4} key={index}>
            <Card 
              size="small"
              hoverable
              style={{ background: '#fafafa', height: '100%' }}
              actions={[
                <Link to={`/classdetailsstudent/${enrollment.groupid}`}>
                  <Button type="link" size="small">
                    {t('viewDetails')}
                  </Button>
                </Link>
              ]}
            >
              <Card.Meta
                title={<Text strong>{enrollment.class_code}</Text>}
                description={
                  <Space direction="vertical" style={{ width: '100%' }} size="small">
                    <div>
                      <Text type="secondary" style={{ fontSize: '12px' }}>{t('Subjects')}:</Text>
                      <div style={{ marginTop: '4px' }}>
                        {enrollment.subjects.slice(0, 2).map((subject, idx) => (
                          <Tag key={idx} size="small" color="blue" style={{ marginBottom: '2px' }}>
                            {subject.title}
                          </Tag>
                        ))}
                        {enrollment.subjects.length > 2 && (
                          <Tag size="small" color="default">
                            +{enrollment.subjects.length - 2} more
                          </Tag>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Text type="secondary" style={{ fontSize: '12px' }}>{t('Total Hours')}:</Text>
                      <Text strong style={{ fontSize: '12px' }}>{enrollment.total_hours} {t('hrs')}</Text>
                    </div>
                  </Space>
                }
              />
            </Card>
          </Col>
        ))}
        {billingHistory.length === 0 && (
          <Col xs={24}>
            <div style={{ 
              textAlign: 'center',
              padding: '40px',
              color: '#8c8c8c'
            }}>
              <TeamOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
              <div style={{ fontSize: '16px' }}>{t('No current enrollments')}</div>
            </div>
          </Col>
        )}
      </Row>
    </Card>
  </Col>

  {/* Bottom Row - Test Scores and Student Info Side by Side */}
  <Col xs={24} lg={12}>
    {/* Test Scores */}
    <Card 
      title={
        <Space>
          <TrophyOutlined />
          {t('testScores')}
          <Badge count={testScores.length} color="#faad14" />
        </Space>
      }
      style={{ height: '400px', borderRadius: '12px' }}
      bodyStyle={{ height: 'calc(100% - 57px)', overflowY: 'auto' }}
    >
      {stats.averageScore > 0 && (
        <Card size="small" style={{ background: '#f0f2ff', marginBottom: '12px' }}>
          <Statistic
            title={t('Average Score')}
            value={stats.averageScore}
            prefix={<StarOutlined />}
            valueStyle={{ color: '#1890ff', fontSize: '20px' }}
          />
        </Card>
      )}
      
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        {testScores.slice(0, 8).map((score, index) => (
          <Card 
            key={index}
            size="small"
            style={{ background: '#fafafa' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <Space direction="vertical" size="small">
                  <Space size="small">
                    <Tag color="blue" size="small">{score.type}</Tag>
                    {score.sub_type && <Text type="secondary" style={{ fontSize: '12px' }}>{score.sub_type}</Text>}
                  </Space>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {formatDate(score.test_date)}
                  </Text>
                </Space>
              </div>
              <Text strong style={{ fontSize: '18px', color: '#1890ff' }}>{score.score}</Text>
            </div>
            {score.note && (
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                {score.note}
              </div>
            )}
          </Card>
        ))}
        {testScores.length === 0 && (
          <div style={{ 
            textAlign: 'center',
            padding: '20px',
            color: '#8c8c8c'
          }}>
            <TrophyOutlined style={{ fontSize: '32px', marginBottom: '8px' }} />
            <div style={{ fontSize: '12px' }}>{t('No test scores available')}</div>
          </div>
        )}
      </Space>
    </Card>
  </Col>

  <Col xs={24} lg={12}>
    {/* Student Info */}
    <Card 
      title={
        <Space>
          <FileTextOutlined />
          {t('studentInfo')}
          <Badge count={studentInfo.length} color="#13c2c2" />
        </Space>
      }
      style={{ height: '400px', borderRadius: '12px' }}
      bodyStyle={{ height: 'calc(100% - 57px)', display: 'flex', flexDirection: 'column' }}
    >
      {/* Student Info List */}
      <div style={{ 
        flex: 1,
        overflowY: 'auto',
        marginBottom: '12px',
        paddingRight: '4px'
      }}>
        <Space direction="vertical" style={{ width: '100%' }} size="small">
          {studentInfo.slice(0, 10).map((info, index) => (
            <Card 
              key={index}
              size="small"
              style={{ background: '#fafafa' }}
              bodyStyle={{ padding: '12px' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1, marginRight: '8px' }}>
                  <Text style={{ 
                    whiteSpace: 'pre-wrap',
                    fontSize: '13px',
                    lineHeight: '1.4'
                  }}>
                    {info.info_text}
                  </Text>
                  <div style={{ marginTop: '8px' }}>
                    <Space size="small">
                      <Tag color="blue" size="small">{info.created_by_nickname}</Tag>
                      <Text type="secondary" style={{ fontSize: '11px' }}>
                        {formatDate(info.created_at)}
                      </Text>
                    </Space>
                  </div>
                </div>
                <Button 
                  type="text" 
                  danger
                  size="small"
                  icon={<DeleteOutlined />} 
                  onClick={() => handleDeleteStudentInfo(info.id)}
                />
              </div>
            </Card>
          ))}
          {studentInfo.length === 0 && (
            <div style={{ 
              textAlign: 'center',
              padding: '20px',
              color: '#8c8c8c'
            }}>
              <FileTextOutlined style={{ fontSize: '32px', marginBottom: '8px' }} />
              <div style={{ fontSize: '12px' }}>{t('No student information available')}</div>
            </div>
          )}
        </Space>
      </div>

      {/* Add New Info Section */}
      <div style={{
        borderTop: '1px solid #f0f0f0',
        paddingTop: '12px',
        background: '#fff'
      }}>
        <Space.Compact style={{ width: '100%' }}>
          <TextArea
            value={newStudentInfo}
            onChange={(e) => setNewStudentInfo(e.target.value)}
            placeholder={t('enterStudentInfo')}
            autoSize={{ minRows: 2, maxRows: 3 }}
            style={{ borderRadius: '6px 0 0 6px', fontSize: '12px' }}
          />
          <Button 
            type="primary"
            onClick={handleAddStudentInfo}
            disabled={!newStudentInfo.trim()}
            style={{ 
              height: 'auto',
              borderRadius: '0 6px 6px 0',
              minHeight: '50px'
            }}
            size="small"
          >
            {t('add')}
          </Button>
        </Space.Compact>
      </div>
    </Card>
  </Col>
</Row>
     
          {/* Additional Info Row - Student Details */}
          <Row style={{ marginTop: '16px' }}>
            <Col span={24}>
              <Card 
                title={
                  <Space>
                    <UserOutlined />
                    {t('Student Details')}
                  </Space>
                }
                style={{ borderRadius: '12px' }}
              >
                <Row gutter={[12, 12]}>
                  <Col xs={24} sm={12} md={8}>
                    <div style={{ padding: '12px', background: '#f5f5f5', borderRadius: '8px' }}>
                      <Space align="center">
                        <MailOutlined style={{ color: '#1890ff', fontSize: '18px' }} />
                        <div>
                          <Text type="secondary" style={{ display: 'block', fontSize: '11px' }}>{t('email')}</Text>
                          <Text strong style={{ fontSize: '13px' }}>{student?.email}</Text>
                        </div>
                      </Space>
                    </div>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <div style={{ padding: '12px', background: '#f5f5f5', borderRadius: '8px' }}>
                      <Space align="center">
                        <PhoneOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
                        <div>
                          <Text type="secondary" style={{ display: 'block', fontSize: '11px' }}>{t('phone')}</Text>
                          <Text strong style={{ fontSize: '13px' }}>{student?.phone}</Text>
                        </div>
                      </Space>
                    </div>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <div style={{ padding: '12px', background: '#f5f5f5', borderRadius: '8px' }}>
                      <Space align="center">
                        <CalendarOutlined style={{ color: '#eb2f96', fontSize: '18px' }} />
                        <div>
                          <Text type="secondary" style={{ display: 'block', fontSize: '11px' }}>{t('dob')}</Text>
                          <Text strong style={{ fontSize: '13px' }}>
                            {student?.date_of_birth && formatDate(student.date_of_birth)}
                          </Text>
                        </div>
                      </Space>
                    </div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <div style={{ padding: '12px', background: '#f5f5f5', borderRadius: '8px' }}>
                      <Space align="center">
                        <HomeOutlined style={{ color: '#faad14', fontSize: '18px' }} />
                        <div>
                          <Text type="secondary" style={{ display: 'block', fontSize: '11px' }}>{t('address')}</Text>
                          <Text strong style={{ fontSize: '13px' }}>{student?.address}</Text>
                        </div>
                      </Space>
                    </div>
                  </Col>
                  <Col xs={24} sm={12}>
                    <div style={{ padding: '12px', background: '#f5f5f5', borderRadius: '8px' }}>
                      <Space align="center">
                        <BookOutlined style={{ color: '#722ed1', fontSize: '18px' }} />
                        <div>
                          <Text type="secondary" style={{ display: 'block', fontSize: '11px' }}>{t('currentEducationLevel')}</Text>
                          <Text strong style={{ fontSize: '13px' }}>{student?.currenteducationlevel}</Text>
                        </div>
                      </Space>
                    </div>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </Content>
      </Layout>
    </Layout>
  );
}

export default withRoleAccess(StudentDashboardAdmin, ['admin', 'superadmin']);